import { useId } from "react";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";
import { isFilled } from "@prismicio/client";

import Styles from "./GapBridge.module.css";

const GapNode = ({ position, label, title, sub }) => {
	// The layout is structurally a pair, but an entirely unfilled node would
	// render as an empty panel with a floating rule -- drop it instead.
	if (!label && !title && !sub) return null;

	return (
		<div className={Styles.node} data-position={position}>
			{label ? <p className={Styles.nodeLabel}>{label}</p> : null}
			<span className={Styles.nodeRule} aria-hidden="true" />
			{title ? <h3 className={Styles.nodeTitle}>{title}</h3> : null}
			{sub ? <p className={Styles.nodeSub}>{sub}</p> : null}
		</div>
	);
};

/**
 * @typedef {import("@prismicio/client").Content.GapBridgeSlice} GapBridgeSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<GapBridgeSlice>} GapBridgeProps
 * @param {GapBridgeProps}
 */
const GapBridge = ({ slice: { primary } }) => {
	const headingId = useId();
	const hasImage = isFilled.image(primary?.background_image);

	return (
		<section
			className={Styles.wrapper}
			data-theme={primary?.dark_theme ? "dark" : "light"}
			data-has-image={hasImage}
			aria-labelledby={headingId}>
			{hasImage && (
				<div className={Styles.media} aria-hidden="true">
					<PrismicNextImage
						field={primary?.background_image}
						fill
						sizes="100vw"
						style={{ objectFit: "cover", objectPosition: "center 40%" }}
					/>
					<div className={Styles.overlay}></div>
				</div>
			)}
			<div className={Styles.container} data-contain="true">
				<h2 id={headingId} className={Styles.srOnly}>
					The response gap TSL fills
				</h2>

				<div className={Styles.track}>
					<GapNode
						position="left"
						label={primary?.left_label}
						title={primary?.left_title}
						sub={primary?.left_sub}
					/>

					{primary?.divider_label ? (
						<div className={Styles.divider}>
							<span className={Styles.dividerLine} aria-hidden="true" />
							<p className={Styles.dividerLabel}>{primary?.divider_label}</p>
							<span className={Styles.dividerLine} aria-hidden="true" />
						</div>
					) : null}

					<GapNode
						position="right"
						label={primary?.right_label}
						title={primary?.right_title}
						sub={primary?.right_sub}
					/>
				</div>

				{primary?.summary?.length ? (
					<div className={Styles.summary}>
						<PrismicRichText field={primary?.summary} />
					</div>
				) : null}
			</div>
		</section>
	);
};

export default GapBridge;
